import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { getCustomerIdentifier } from '../models/customer.model';
import { IBaryTableFetchService } from './i-bary-table-fetch-service';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { CustomerViewDto } from '../models/customer-view-dto.model';
import { mapAsBody } from '../app/shared/util/request-util';

type EntityType = CustomerViewDto;
type EntityResponseType = HttpResponse<EntityType>;
type EntityArrayResponseType = HttpResponse<EntityType[]>;

@Injectable({ providedIn: 'root' })
export class CustomerService implements IBaryTableFetchService<EntityType> {
  protected customerUrl = API_ENDPOINTS.CUSTOMER;
  protected customerDataUrl = API_ENDPOINTS.CUSTOMER_DATA;
  protected customerIdUrl = API_ENDPOINTS.CUSTOMER_ID;
  protected initCustomerResourceUrl = API_ENDPOINTS.CUSTOMER_CURRENT;
  protected customerUserInformationUrl = API_ENDPOINTS.CUSTOMER_ID_USER;
  protected customerDeleteAlias = API_ENDPOINTS.CUSTOMER_USER_ID_DELETE_ALIAS;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService, protected apiProvider: ApiProviderService) {}

  create(entity: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(entity);
    return this.apiProvider.post<EntityType>(this.customerUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(customer: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(customer);
    return this.apiProvider.put<EntityType>(this.customerIdUrl(getCustomerIdentifier(customer)), copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  updateWithUserInformation(customer: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(customer);
    return this.apiProvider
      .put<EntityType>(this.customerUserInformationUrl(getCustomerIdentifier(customer)), copy)
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(customer: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(customer);
    return this.apiProvider.patch<EntityType>(this.customerIdUrl(getCustomerIdentifier(customer)), copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.apiProvider.get<EntityType>(this.customerIdUrl(id)).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<HttpResponse<CustomerViewDto[]>> {
    const options = createRequestOption(req);
    return this.apiProvider
      .get<CustomerViewDto[]>(this.customerUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  // /!\ PUBLIC
  findCurrentCustomer(): Observable<EntityType | undefined> {
    return this.apiProvider.publicGet<EntityType>(this.initCustomerResourceUrl()).pipe(map(httpResponse => httpResponse.body ?? undefined));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete(this.customerIdUrl(id));
  }

  deleteAlias(id: number): Observable<EntityType> {
    return this.apiProvider.post<EntityType>(this.customerDeleteAlias(id), undefined).pipe(mapAsBody);
  }

  addCustomerToCollectionIfMissing(customerCollection: EntityType[], ...customersToCheck: (EntityType | null | undefined)[]): EntityType[] {
    const customers: EntityType[] = customersToCheck.filter(isPresent);
    if (customers.length > 0) {
      const customerCollectionIdentifiers = customerCollection.map(customerItem => getCustomerIdentifier(customerItem));
      const customersToAdd = customers.filter(customerItem => {
        const customerIdentifier = getCustomerIdentifier(customerItem);
        if (customerIdentifier == null || customerCollectionIdentifiers.includes(customerIdentifier)) {
          return false;
        }
        customerCollectionIdentifiers.push(customerIdentifier);
        return true;
      });
      return [...customersToAdd, ...customerCollection];
    }
    return customerCollection;
  }

  fillDataToShow(entities: EntityType[]): Observable<EntityType[]> {
    return of(entities);
  }

  protected convertDateFromClient(customer: EntityType): EntityType {
    return Object.assign({}, customer, {
      creationDate: dayjs(customer.creationDate).isValid() ? dayjs(customer.creationDate).toJSON() : undefined,
    });
  }

  convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
    }
    return res;
  }

  convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((customer: EntityType) => {
        customer.creationDate = customer.creationDate ? dayjs(customer.creationDate) : undefined;
      });
    }
    return res;
  }

  hasIncompleteData(): Observable<boolean> {
    return this.apiProvider.publicGet<boolean>(this.customerDataUrl).pipe(map(httpResponse => httpResponse.body ?? false));
  }
}
